// @wc-ignore-file
/**
 * CR-171: the chat composer's keyboard rules (send / newline) and the line
 * break insertion Ctrl/Cmd+Enter needs. Pure logic; the directive above keeps
 * wuchale away from the key literals.
 */
import { KEY_ENTER } from "@/lib/keyboard-keys";

/** The fields of a keydown the chat composer's key rules read. */
export interface ComposerKeyEvent {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  keyCode?: number;
  nativeEvent?: { isComposing?: boolean };
}

/** An IME composition is in progress: its Enter confirms the composition. Safari reports it through `keyCode === 229` only. */
function isComposing(event: ComposerKeyEvent): boolean {
  return Boolean(event.nativeEvent?.isComposing) || event.keyCode === 229;
}

/**
 * CR-171: does this keydown send the chat message being written?
 *
 * Plain Enter only. Shift+Enter falls through so the textarea inserts the
 * line break itself; Ctrl/Cmd/Alt+Enter is a newline too
 * (see `isNewlineShortcut` — unlike document comments, where CR-142 made
 * Ctrl/Cmd+Enter send).
 *
 * Never sends while the @mention menu is about to pick a highlighted member
 * (`isMentionPickPending`; `@diceui/mention` runs the
 * app's keydown FIRST and skips its own when the event is default-prevented,
 * so sending there would post a half-typed `@Ad`) or while an IME composition
 * is in progress.
 *
 * Key names come from `@/lib/keyboard-keys`: wuchale would translate an inline
 * `"Enter"` literal under fr/de.
 */
export function isChatSendKey(
  event: ComposerKeyEvent,
  options: { mentionPickPending: boolean },
): boolean {
  if (event.key !== KEY_ENTER) {
    return false;
  }
  if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
    return false;
  }
  return !options.mentionPickPending && !isComposing(event);
}

/**
 * CR-171: Ctrl+Enter, Cmd+Enter or Alt/Option+Enter inserts a line break in
 * the chat composer. A textarea inserts nothing natively for them (Alt+Enter
 * included, seen in Chrome on dev), so the caller does it with
 * `insertNewlineAtCaret`. Left to diceui while it is about to pick a member.
 */
export function isNewlineShortcut(
  event: ComposerKeyEvent,
  options: { mentionPickPending: boolean },
): boolean {
  if (event.key !== KEY_ENTER || !(event.ctrlKey || event.metaKey || event.altKey)) {
    return false;
  }
  return !options.mentionPickPending && !isComposing(event);
}

/**
 * Inserts a line break at the caret (replacing any selection), as typing it
 * would: `execCommand("insertText")` keeps the browser's undo stack and fires
 * the native `input` event React listens to. Where it is unavailable or
 * refuses, the value is written through the prototype's `value` setter —
 * React's value tracker wraps the element's own setter, so going around it is
 * what makes React see the change — and an `input` event is dispatched so the
 * `onChange` handlers run.
 */
export function insertNewlineAtCaret(textarea: HTMLTextAreaElement): void {
  const inserted =
    typeof document.execCommand === "function" && document.execCommand("insertText", false, "\n");
  if (inserted) {
    return;
  }
  const { selectionStart: start, selectionEnd: end, value } = textarea;
  const next = `${value.slice(0, start)}\n${value.slice(end)}`;
  const setValue = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
  if (setValue) {
    setValue.call(textarea, next);
  } else {
    textarea.value = next;
  }
  textarea.setSelectionRange(start + 1, start + 1);
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}
